/* eslint-disable @reatom/unit-naming-rule */
import { action, atom, onConnect } from '@reatom/framework'
import { withLocalStorage } from '@reatom/persist-web-storage'

import { Result } from '@blade/std-lib'
import { dependenciesAtom } from '@root/features/shared/dependencies'
import { ApiError, apiRequest } from '@root/shared/api'
import { STORAGE_PREFIX } from '@root/shared/constants'
import { reatomRequest } from '@root/shared/lib'

/**
 * Which way the trending column is ranked.
 *
 * The names come from the server rather than this file, so a third way to
 * rank appears in the selector by deploying the server. What is kept here is
 * only the choice, and it is kept per browser: a list ranked one way is a
 * different answer to the same question, and having to pick it again on every
 * visit would make comparing them a chore.
 */
export const DEFAULT_TRENDING_STRATEGY = 'kol.trade'

export const trendingStrategyAtom = atom(
  DEFAULT_TRENDING_STRATEGY,
  'trendingStrategyAtom',
).pipe(withLocalStorage(`${STORAGE_PREFIX}/v1/trendingStrategy`))

export const setTrendingStrategyAction = action((ctx, id: string) => {
  trendingStrategyAtom(ctx, id)
}, 'setTrendingStrategyAction')

const fetchTrendingStrategies = action(async (ctx) => {
  const { tradeApiClient } = ctx.get(dependenciesAtom)

  const result = await ctx.schedule(() =>
    apiRequest(ctx, tradeApiClient.services.servicesTrendingStrategiesList, []),
  )

  return result.pipe(Result.mapError(ApiError.fallbackToUnknown))
}, 'fetchTrendingStrategies')

export const trendingStrategiesStateAtom = reatomRequest({
  name: 'trendingStrategiesState',
  execAction: fetchTrendingStrategies,
})

// Read once, when something first wants to draw the selector. The answer is
// a list of names that changes on a deploy, not on a tick.
onConnect(trendingStrategiesStateAtom, (ctx) => {
  trendingStrategiesStateAtom.execute(ctx)
})

/** Whether the list on screen is the one the socket pushes. */
export const trendingIsLiveAtom = atom(
  (ctx) => ctx.spy(trendingStrategyAtom) === DEFAULT_TRENDING_STRATEGY,
  'trendingIsLiveAtom',
)

/**
 * A beat for the lists the socket does not push.
 *
 * Only the other strategies need it, and only while one of them is on
 * screen — a list nobody is looking at does not need refreshing, and ours
 * already arrives by itself. Spying this is what makes a column re-read.
 */
const POLL_MS = 30_000

export const trendingPollAtom = atom(0, 'trendingPollAtom')

onConnect(trendingPollAtom, (ctx) => {
  const timer = window.setInterval(() => {
    if (!ctx.get(trendingIsLiveAtom)) {
      trendingPollAtom(ctx, (tick) => tick + 1)
    }
  }, POLL_MS)

  return () => {
    window.clearInterval(timer)
  }
})
